import { useState } from 'react';
import { Check, ClipboardList, Camera } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  Progress,
  Skeleton,
  StatusBadge,
  SubBar,
} from '@/design/components';
import { useMe } from '@/auth/useMe';
import { errorMessage } from '@/lib/errors';
import { uploadOpsPhoto, useOpsPhoto } from './photos';
import {
  remainingLabel,
  useChecklistMutations,
  useChecklistTemplates,
  useTodayRun,
  type ChecklistItem,
} from './api';

/**
 * צ'קליסט המשמרת (PRD 5.9.1).
 *
 * רשימה אחת ליום לכל סניף: מי שפותח ראשון פותח, והשני ממשיך את אותה
 * רשימה. ככה לא מתקבלות שתי רשימות חצי מלאות על אותה פתיחה.
 */
export function ChecklistsPage() {
  const { me } = useMe();
  const branchId = me?.primaryBranchId ?? null;
  const templates = useChecklistTemplates(branchId);
  const [openId, setOpenId] = useState<string | null>(null);

  if (templates.isPending) return <Skeleton className="m-4 h-40" />;
  if (templates.isError) return <ErrorState onRetry={() => void templates.refetch()} />;

  const all = templates.data ?? [];

  return (
    <div className="grid content-start gap-4">
      <SubBar title="צ׳קליסטים" subtitle="מה עושים בפתיחה ובסגירה" />

      <div className="grid gap-4 px-4 pb-8">
        {!branchId && (
          <EmptyState
            title="אין לך סניף"
            description="צ׳קליסט שייך לסניף. פנה/י למנהל/ת כדי לשייך אותך."
          />
        )}

        {branchId && all.length === 0 && (
          <EmptyState
            title="אין צ׳קליסטים"
            description="כשההנהלה תגדיר צ׳קליסט לסניף הוא יופיע כאן."
          />
        )}

        {all.map((template) => (
          <Checklist
            key={template.id}
            templateId={template.id}
            name={template.name}
            dueTime={template.due_time}
            branchId={branchId}
            isOpen={openId === template.id}
            onToggle={() => setOpenId(openId === template.id ? null : template.id)}
          />
        ))}
      </div>
    </div>
  );
}

function Checklist({
  templateId,
  name,
  dueTime,
  branchId,
  isOpen,
  onToggle,
}: {
  templateId: string;
  name: string;
  /** HH:MM:SS, או null כשאין מועד. */
  dueTime: string | null;
  branchId: string | null;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const data = useTodayRun(templateId, branchId);
  const { open, setItem, complete } = useChecklistMutations();
  const [error, setError] = useState<string | null>(null);
  const [busyItem, setBusyItem] = useState<string | null>(null);

  const run = data.data?.run ?? null;
  const items = data.data?.items ?? [];
  const runItems = data.data?.runItems ?? [];
  const doneCount = runItems.filter((item) => item.is_done).length;
  const finished = run?.completed_at != null;

  const run_ = (action: () => Promise<unknown>) => {
    setError(null);
    void action()
      .then(() => void data.refetch())
      .catch((caught) => setError(errorMessage(caught)));
  };

  const start = () => run_(() => open.mutateAsync(templateId));

  const toggle = (item: ChecklistItem, next: boolean) => {
    if (!run) return;
    run_(() => setItem.mutateAsync({ runId: run.id, itemId: item.id, isDone: next }));
  };

  const attach = (item: ChecklistItem, file: File) => {
    if (!run || !branchId) return;
    setBusyItem(item.id);
    setError(null);
    uploadOpsPhoto(branchId, 'checklist', file)
      .then((path) =>
        setItem.mutateAsync({ runId: run.id, itemId: item.id, isDone: true, photoPath: path }),
      )
      .then(() => void data.refetch())
      .catch((caught) => setError(errorMessage(caught)))
      .finally(() => setBusyItem(null));
  };

  return (
    <section className="grid gap-3 rounded-md border border-line bg-raised p-3">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 bg-transparent text-start"
      >
        <ClipboardList size={20} strokeWidth={1.75} aria-hidden="true" />
        <span className="grid grow gap-0.5">
          <span className="text-title font-medium text-ink">{name}</span>
          {dueTime && (
            // העובד צריך לדעת עד מתי, לא רק מה
            <span className="text-label text-muted">
              עד{' '}
              <span className="tabular" dir="ltr">
                {dueTime.slice(0, 5)}
              </span>
            </span>
          )}
        </span>
        {finished ? (
          <StatusBadge tone="good">הושלם</StatusBadge>
        ) : run ? (
          <StatusBadge tone="warn">בתהליך</StatusBadge>
        ) : (
          <span className="text-body-sm text-muted">לא התחיל</span>
        )}
      </button>

      {isOpen && (
        <div className="grid gap-3">
          {data.isPending && <Skeleton className="h-24" />}

          {error && (
            <p role="alert" className="m-0 text-body-sm text-bad">
              {error}
            </p>
          )}

          {!run && !data.isPending && (
            <div>
              <Button variant="primary" disabled={open.isPending} onClick={start}>
                להתחיל את הצ׳קליסט
              </Button>
            </div>
          )}

          {run && (
            <>
              <Progress
                value={doneCount}
                max={items.length}
                label={remainingLabel(doneCount, items.length)}
              />

              <ul className="m-0 grid list-none gap-2 p-0">
                {items.map((item) => {
                  const state = runItems.find((entry) => entry.item_id === item.id);
                  return (
                    <li key={item.id}>
                      <ChecklistRow
                        item={item}
                        isDone={state?.is_done ?? false}
                        photoPath={state?.photo_path ?? null}
                        disabled={finished}
                        busy={busyItem === item.id}
                        onToggle={(next) => toggle(item, next)}
                        onPhoto={(file) => attach(item, file)}
                      />
                    </li>
                  );
                })}
              </ul>

              {!finished && (
                <Button
                  variant="primary"
                  disabled={complete.isPending}
                  onClick={() => run_(() => complete.mutateAsync(run.id))}
                >
                  <Check size={20} strokeWidth={1.75} aria-hidden="true" />
                  סיימתי, לסגור את הצ׳קליסט
                </Button>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

function ChecklistRow({
  item,
  isDone,
  photoPath,
  disabled,
  busy,
  onToggle,
  onPhoto,
}: {
  item: ChecklistItem;
  isDone: boolean;
  photoPath: string | null;
  disabled: boolean;
  busy: boolean;
  onToggle: (next: boolean) => void;
  onPhoto: (file: File) => void;
}) {
  const photo = useOpsPhoto(photoPath);

  return (
    <div className="grid gap-2 rounded-sm border border-line p-2">
      <label className="flex items-center gap-2 text-body">
        <input
          type="checkbox"
          checked={isDone}
          disabled={disabled || busy}
          onChange={(event) => onToggle(event.target.checked)}
          className="size-5 shrink-0"
        />
        <span className="grow">{item.text}</span>
        {item.requires_photo && !photoPath && (
          <span className="shrink-0 text-label text-warn">צריך צילום</span>
        )}
      </label>

      {item.requires_photo && !disabled && (
        <label className="flex items-center gap-2 text-body-sm text-muted">
          <Camera size={18} strokeWidth={1.75} aria-hidden="true" />
          {busy ? 'מעלה…' : photoPath ? 'להחליף צילום' : 'לצלם'}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onPhoto(file);
            }}
            className="sr-only"
          />
        </label>
      )}

      {photo.data && (
        <img src={photo.data} alt={item.text} className="max-h-48 w-full rounded-sm object-cover" />
      )}
    </div>
  );
}
